/**
 * Customer-language templates for record-family findings (inc5 step F,
 * customer-loop scope §4). Deterministic string templates over the finding /
 * preview data the records already persist — no prose generation, no raw
 * check or correction ids, never "AI". React-free; tested under the
 * functions package's vitest (`npm run test:web`).
 */

/** "AE|AESEV" → "AESEV" — the field name a data manager recognizes. */
export function fieldNameOfKey(key: string): string {
  const idx = key.indexOf('|');
  return idx >= 0 ? key.slice(idx + 1) : key;
}

/**
 * The AESEV-class offer line, per preview entry:
 *   "The dictionary says AESEV maps to AE.AESEV, but the SDTM mapping says
 *    AE.AETOXGR. Review which is correct."
 */
export function mappingOfferLine(key: string, was: string, now: string): string {
  const field = fieldNameOfKey(key);
  const ddSide =
    was === '(none)' || was.trim() === ''
      ? `The dictionary has no SDTM assignment for ${field}`
      : `The dictionary says ${field} maps to ${was}`;
  return `${ddSide}, but the SDTM mapping says ${now}. Review which is correct.`;
}

/** What approving does — shown once under the offer's lines. */
export function mappingOfferAction(): string {
  return 'Approving updates the dictionary to match the mapping; the previous values are recorded and recoverable.';
}

/**
 * Ambiguity guidance. `variantsJoined` arrives as the record persists it
 * ("A vs B"); rendered with "and" for reading.
 */
export function ambiguityLine(key: string, variantsJoined: string): string {
  const field = fieldNameOfKey(key);
  const variants = variantsJoined.split(' vs ').join(' and ');
  return (
    `The SDTM mapping has rows for ${field} that disagree (${variants}). ` +
    "The dictionary can't be corrected until the mapping agrees."
  );
}

/** S1 staleness INTO the DD — the regenerate guidance. */
export function ddStaleLine(): string {
  return 'The case report forms changed after the data dictionary was generated. Regenerate the dictionary to pick up the changes.';
}

/** Customer display names for the prose documents (never raw doc keys). */
export const PROSE_DOC_NAMES: Record<string, string> = {
  dmp: 'Data Management Plan',
  edts: 'External Data Transfer Specification',
  medicalCodingSpec: 'Medical Coding Specification',
  drcp: 'Data Review and Cleaning Plan',
  saerp: 'SAE Reconciliation Procedure',
  dblc: 'Database Lock Checklist',
};
const docName = (key: string) => PROSE_DOC_NAMES[key] ?? key;

const COUNT_CLAIM_PHRASES: Record<string, [string, string]> = {
  ddFieldCount: ['the dictionary had', 'fields'],
  ddFormCount: ['the dictionary covered', 'forms'],
  editCheckCount: ['the study had', 'edit checks'],
};

export interface ProseFindingLike {
  kind?: string;
  subject?: string;
  data?: Record<string, string>;
}

/**
 * Deterministic customer line for an M/IC finding (inc6 step D) — templates
 * over the structured item data the checks persist. Null for kinds this
 * layer does not render (builder-side classes).
 */
export function proseFindingLine(item: ProseFindingLike): string | null {
  const d = item.data ?? {};
  switch (item.kind) {
    case 'count-drift': {
      const [verb, noun] = COUNT_CLAIM_PHRASES[d.claim] ?? ['the study had', d.claim];
      return `The ${docName(d.doc)} was written when ${verb} ${d.was} ${noun}; it now has ${d.now}. Regenerate it to match.`;
    }
    case 'form-removed':
      return `The ${docName(d.doc)} was written from the form "${d.name}" (${d.code}), which is no longer in the study. Regenerate it to match.`;
    case 'form-added':
      return `Form "${d.name}" (${d.code}) was added after the ${docName(d.doc)} was written. Regenerate it to match.`;
    case 'form-renamed':
      return `The ${docName(d.doc)} was written when ${d.code} was named "${d.was}"; it is now "${d.now}". Regenerate it to match.`;
    case 'schedule-drift':
      return `The ${docName(d.doc)} was written against a ${d.was}-visit schedule; the schedule now has ${d.now} visits. Regenerate it to match.`;
    case 'stale-name':
      return `The ${docName(d.doc)} still references "${d.name}", which is no longer in the study.`;
    case 'duplicate-mapping-rows': {
      const field = fieldNameOfKey(item.subject ?? '');
      const variants = (d.variants ?? '').split(' vs ').join(' and ');
      return `The SDTM mapping has rows for ${field} that disagree (${variants}). Remove or reconcile them.`;
    }
    case 'codelist-on-result-field':
      return `${d.field} on the ${d.code} form collects a value but carries the test-code codelist ${d.codelist}. A result field takes its own attributes; remove the codelist reference.`;
    case 'consent-visit-drift':
      return `The consent form’s "${d.section}" section was written against a ${d.was}-visit schedule; the schedule now has ${d.now} visits. Regenerate the section.`;
    case 'consent-stale-visit':
      return `The consent form’s "${d.section}" section still names the visit "${d.name}", which is no longer in the schedule.`;
    case 'codelist-on-specify-other':
      return `${d.field} on the ${d.code} form is a specify-other text field but carries the codelist ${d.codelist}. The parent picklist owns it; remove the codelist reference.`;
    case 'generation-finding':
      return d.line ?? null;
    default:
      return null;
  }
}

/** Disclosure line for the applied not-submitted clears (never a decision). */
export function autoClearDisclosure(n: number): string {
  return n === 1
    ? 'Cleared the SDTM assignment on 1 dictionary field whose mapping marks it not submitted.'
    : `Cleared the SDTM assignment on ${n} dictionary fields whose mapping marks them not submitted.`;
}

/** A correction's per-outcome counts, as the fixers persist them. */
export type CorrectionSummaryLike = Record<string, number> | undefined;

const count = (s: CorrectionSummaryLike, k: string) => Number(s?.[k] ?? 0) || 0;

/**
 * Every line the applied not-submitted clear should disclose, from the whole
 * summary the fixer persists: what was cleared, what was KEPT because the
 * sponsor's uploaded dictionary supplied it (and, of those, where the other
 * half of the assignment was still cleared), and entries the correction was
 * planned for that no longer exist. Empty when there is nothing to say — never
 * "Cleared … on 0 dictionary fields".
 */
export function autoClearDisclosureLines(summary: CorrectionSummaryLike): string[] {
  const lines: string[] = [];
  const cleared = count(summary, 'cleared');
  const kept = count(summary, 'sponsorProtected');
  const partly = count(summary, 'partlyCleared');
  const gone = count(summary, 'notFound');
  if (cleared > 0) lines.push(autoClearDisclosure(cleared));
  if (kept > 0) {
    lines.push(
      kept === 1
        ? 'Kept the SDTM assignment your uploaded dictionary supplied on 1 dictionary field, although its mapping marks it not submitted.'
        : `Kept the SDTM assignment your uploaded dictionary supplied on ${kept} dictionary fields, although their mapping marks them not submitted.`,
    );
  }
  if (partly > 0) {
    lines.push(
      `On ${partly} of those field${partly === 1 ? '' : 's'}, the part of the assignment your dictionary did not supply was cleared.`,
    );
  }
  if (gone > 0) {
    lines.push(
      gone === 1
        ? '1 dictionary field this correction was planned for no longer exists, so nothing was changed on it.'
        : `${gone} dictionary fields this correction was planned for no longer exist, so nothing was changed on them.`,
    );
  }
  return lines;
}

/** The toast after a person approves a correction, with what it did not change. */
export function approvedCorrectionToast(summary: CorrectionSummaryLike): string {
  const parts = ['Correction applied — the previous values are recorded and recoverable.'];
  const kept = count(summary, 'sponsorProtected');
  const gone = count(summary, 'notFound');
  if (kept > 0) {
    parts.push(
      kept === 1
        ? '1 field kept the value your uploaded dictionary supplied.'
        : `${kept} fields kept the value your uploaded dictionary supplied.`,
    );
  }
  if (gone > 0) {
    parts.push(
      gone === 1
        ? '1 field was no longer in the dictionary and was not changed.'
        : `${gone} fields were no longer in the dictionary and were not changed.`,
    );
  }
  return parts.join(' ');
}
